import React from 'react';
import { jobLocationLabel } from '../../utils/jobLocation';


// Previous Salary Formatting Function
// const formatSalary = (jobdetails) => {
//   const sym = jobdetails?.currency_symbol;
//   const budget = jobdetails?.project_budget;
//   if (budget && sym) return `${sym}${budget}`;
//   return budget || jobdetails?.salary_range || 'N/A';
// };

const formatSalary = (jobdetails) => {

  // Get the currency symbol
  const sym = jobdetails?.currency_symbol;

  // Get the budget
  const budget = jobdetails?.project_budget;

  // place the currency symbol before the amount and convert the budget to a number and add comma separators
  if (budget && sym) {
    return `${sym}${Number(budget).toLocaleString()}`;
  }

  // still format the number with comma separators even if no currency symbol
  if (budget) {
    return Number(budget).toLocaleString();
  }

  // If no salary/budget information is available, display N/A.
  return 'N/A';
};

const JobDetails = ({jobdetails}) => {
  const description = (jobdetails?.jobDetails || '').trim();
  const [descriptionOpen, setDescriptionOpen] = React.useState(false);
  const [canExpandDescription, setCanExpandDescription] = React.useState(false);
  const descriptionRef = React.useRef(null);

  /* Whether "See more" is worth showing is a question about the rendered box,
     not the string: five lines holds a different number of characters at
     every width. Measured while collapsed — once expanded, scrollHeight and
     clientHeight match and the check would answer no. */
  React.useEffect(() => {
    const el = descriptionRef.current;
    if (!el || descriptionOpen) return;
    setCanExpandDescription(el.scrollHeight > el.clientHeight + 1);
  }, [description, descriptionOpen]);

  return (
    <div>
      <h2 className='text-xl lg:text-2xl font-semibold leading-8 mb-3.5'>
        Job details
      </h2>

      <div className='mt-6 flex items-center gap-6 pb-4'>
        <h3 className='text-xl font-semibold'>Job Title:</h3>
        <p className='lg:text-lg text-muted-foreground'>{jobdetails?.jobTitle || 'N/A'}</p>
      </div>

      <div className='gap-[30px] grid xl:grid-cols-4 md:grid-cols-3 grid-cols-2 '>
        <div className='flex items-center gap-9'>
          <h3 className='text-xl font-semibold'>Salary:</h3>
          <p className='lg:text-lg text-muted-foreground'>{formatSalary(jobdetails)}</p>
        </div>
        <div className='flex items-center gap-9'>
          <h3 className='text-xl font-semibold'>Application link:</h3>
          {jobdetails?.id ? (
            <a
              href={`${process.env.REACT_APP_JOB_BOARD_URL || 'https://job-board.droomwork.io'}/job/${jobdetails?.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className='lg:text-lg text-blue-600 hover:underline'
            >
              Link
            </a>
          ) : (
            <p className='lg:text-lg text-muted-foreground'>N/A</p>
          )}
        </div>
        <div className='flex items-center gap-9'>
          <h3 className='text-xl font-semibold'>Posted date:</h3>
          <p className='lg:text-lg text-muted-foreground'>{new Date(jobdetails?.postedDate).toLocaleDateString()}</p>
        </div>
        <div className='flex items-center gap-9'>
          <h3 className='text-xl font-semibold'>Work type:</h3>
          <p className='lg:text-lg text-muted-foreground'>{jobdetails?.workType}</p>
        </div>
        <div className='flex items-center gap-9'>
          <h3 className='text-xl font-semibold'>Job location:</h3>
          <p className='lg:text-lg text-muted-foreground'>{jobLocationLabel(jobdetails)}</p>
        </div>

        <div className='flex items-center gap-9'>
          <h3 className='text-xl font-semibold'>Experience</h3>
          <p className='lg:text-lg text-muted-foreground'>{jobdetails?.level}</p>
        </div>
        {jobdetails?.matching_info && (
          <div className='flex items-start gap-9'>
            <h3 className='text-xl font-semibold whitespace-nowrap'>Matching:</h3>
            {/* "This match is 100% accurate ... · Location: 3 talents in Port Harcourt, 0 more in Rivers ..." */}
            <div className='lg:text-lg text-muted-foreground'>
              {String(jobdetails.matching_info).split(' · ').map((part, i) => (
                <p key={i}>{part}</p>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className='mt-6'>
        <h3 className='text-xl font-semibold mb-2'>Job Description:</h3>

        {/* The wizard takes the description as a file upload and the API
            extracts its text into jobDetails, because the public board, the
            job feed and the AI flyer all need something to read. Printed
            straight into the panel that text reads as a wall, which is what
            made a posted job look like it had been "written out" — so it
            opens at five lines.

            "See more" expands it in place rather than linking away. The link
            to the file sits under the text, for the original formatting and
            for anything extraction could not reach. */}
        {description && (
          <p
            ref={descriptionRef}
            className={`lg:text-lg text-muted-foreground whitespace-pre-line ${
              descriptionOpen ? '' : 'line-clamp-5'
            }`}
          >
            {description}
          </p>
        )}

        {canExpandDescription && (
          <button
            type='button'
            onClick={() => setDescriptionOpen((v) => !v)}
            className='mt-2 block text-sm font-semibold text-blue-600 hover:underline'
          >
            {descriptionOpen ? 'See less' : 'See more'}
          </button>
        )}

        {jobdetails?.jobDescription ? (
          <a
            href={jobdetails.jobDescription}
            target="_blank"
            rel="noopener noreferrer"
            className='mt-2 inline-block text-sm font-semibold text-blue-600 hover:underline'
          >
            Open the uploaded job description
          </a>
        ) : (
          !description && (
            <p className='lg:text-lg text-muted-foreground'>N/A</p>
          )
        )}
      </div>

    </div>
  );
};

export default JobDetails;